import React, { useState } from 'react';
import { Box, Stack, Typography, Checkbox, Button, TextField, CircularProgress, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Rating as MuiRating, Avatar as MuiAvatar } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import StarRatingIcon from '../icons/StarRatingIcon';
import GoogleIcon from '../icons/GoogleIcon';
import ReplyIcon from '../icons/ReplyIcon';
import AIIcon from '../icons/AIIcon';
import SendIcon from '../icons/SendIcon';
import * as OpenAI from '../../services/gmbRanking/geminiService';
import GMBService from '../../services/GMBService';

import logger from '../../utils/logger';
const ReviewContainer = styled(Stack)(({ theme }) => ({
  gap: '16px',
  [theme.breakpoints.up('sm')]: {
    gap: '24px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '34px'
  }
}));

const ReviewContent = styled(Stack)(({ theme }) => ({
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gap: '16px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px'
  }
}));

const ReviewHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '10px',
  [theme.breakpoints.up('sm')]: {
    gap: '14px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px'
  }
}));

const CheckboxContainer = styled(Box)(({ theme }) => ({
  marginTop: '4px',
  [theme.breakpoints.up('md')]: {
    marginTop: '8px'
  }
}));

const Avatar = styled('img')(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
  [theme.breakpoints.up('sm')]: {
    width: '48px',
    height: '48px'
  },
  [theme.breakpoints.up('md')]: {
    width: '56px',
    height: '56px'
  }
}));

const UserInfo = styled(Stack)(({ theme }) => ({
  gap: '3px'
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '18px'
  }
}));

const ReviewDate = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#0b91d6',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const RatingRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  marginTop: '8px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {
    marginTop: '16px'
  }
}));

const RatingText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const FromText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  marginLeft: '3px',
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'inline',
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const ReviewText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#121927',
  marginLeft: '0',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    lineHeight: '22px',
    marginLeft: '62px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    lineHeight: '24px',
    marginLeft: '72px'
  }
}));

const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: '#f6f0f0',
  border: '1px solid #f6f0f0',
  marginLeft: '0',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '62px'
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '72px'
  }
}));

const ActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'stretch',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '16px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px'
  }
}));

const ActionButtons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '10px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    gap: '14px',
    flexWrap: 'nowrap'
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px'
  }
}));

const ReplyButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0b91d6',
  borderRadius: '8px',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  flex: 1,
  minWidth: '90px',
  minHeight: '40px',
  textTransform: 'capitalize',
  padding: '8px 12px',
  gap: '6px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: '#0875a8'
  },
  [theme.breakpoints.up('sm')]: {
    flex: 'none',
    width: '120px',
    fontSize: '13px',
    padding: '8px 14px'
  },
  [theme.breakpoints.up('md')]: {
    width: '140px',
    minHeight: '44px',
    fontSize: '14px',
    padding: '8px 16px',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#0980c2'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const AIReplyButton = styled(Button)(({ theme }) => ({
  border: '0.6px solid #a0a0aa',
  borderRadius: '8px',
  color: '#0b91d6',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'capitalize',
  padding: '8px 12px',
  gap: '6px',
  flex: 1,
  minWidth: '90px',
  minHeight: '40px',
  backgroundColor: 'transparent',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: 'rgba(11, 145, 214, 0.08)'
  },
  [theme.breakpoints.up('sm')]: {
    flex: 'none',
    width: 'auto',
    fontSize: '13px',
    padding: '8px 14px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    padding: '8px 16px',
    gap: '8px',
    '&:hover': {
      backgroundColor: 'rgba(11, 145, 214, 0.04)'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const CreatePostText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff'
}));

const ReplyInput = styled(TextField)(({ theme }) => ({
  backgroundColor: 'rgba(160, 160, 170, 0.10)',
  borderRadius: '8px',
  marginLeft: '0',
  width: '100%',
  opacity: 1,
  minHeight: '50px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    outline: 'none',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' }
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    color: '#a0a0aa',
    padding: '12px',
    textAlign: 'center',
    outline: 'none',
    '::placeholder': {
      color: '#a0a0aa',
      opacity: 1,
      textAlign: 'center'
    }
  },
  '& textarea': {
    textAlign: 'center',
    outline: 'none',
    '::placeholder': {
      color: '#a0a0aa',
      opacity: 1,
      textAlign: 'center'
    }
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: '62px',
    width: 'calc(100% - 62px)',
    '& .MuiInputBase-input': {
      padding: '14px'
    }
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '72px',
    width: '1150px',
    '& .MuiInputBase-input': {
      padding: '16px'
    }
  }
}));

const SendButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '30px',
  height: '30px',
  borderRadius: '34px',
  backgroundColor: '#0b91d6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
}));

const ReviewCard = ({ review, selected, onSelect, onAIReply, onCreatePost, businessInfo, onReplyUpdate }) => {
  const navigate = useNavigate();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [deletingReply, setDeletingReply] = useState(false);
  const [isEditingReply, setIsEditingReply] = useState(false);

  const handleReplyClick = (event) => {
    // Prevent event from bubbling up to parent components
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    logger.log('[ReviewCard] Reply clicked, opening dialog');
    setIsEditingReply(false);
    setReplyDialogOpen(true);
    setReplyText(review.reply || '');
  };

  const handleCloseReplyDialog = () => {
    setReplyDialogOpen(false);
    setReplyText('');
    setIsEditingReply(false);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    try {
      setSubmittingReply(true);

      // Get access token
      const accessToken = await GMBService.getAccessToken();

      logger.log(`[ReviewCard] ${isEditingReply ? 'Updating' : 'Submitting'} reply:`, replyText, 'for review:', review.id);

      // Submit or update reply to GMB (same endpoint handles both)
      await GMBService.replyToReview(accessToken, review.id, replyText);

      alert(`Reply ${isEditingReply ? 'updated' : 'submitted'} successfully!`);

      // Trigger refresh if callback provided
      if (onReplyUpdate) {
        onReplyUpdate();
      }

      handleCloseReplyDialog();
    } catch (error) {
      logger.error('Error submitting reply:', error);
      alert(`Failed to ${isEditingReply ? 'update' : 'submit'} reply. Please try again.`);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleAIReply = async (event) => {
    // Prevent event from bubbling up to parent components
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    logger.log('[ReviewCard] AI Reply clicked, opening dialog');

    // Open the dialog first
    setReplyDialogOpen(true);
    setReplyText(review.reply || '');

    // Then generate AI reply automatically
    try {
      setGeneratingAI(true);

      logger.log('🤖 [OpenAI] Generating reply for review:', review.author);

      let tone = review.rating >= 4 ? 'grateful' : review.rating === 3 ? 'professional' : 'apologetic';

      const bi = businessInfo || { name: 'our business', category: 'business' };

      logger.log('📤 [OpenAI] Sending request with:', {
        businessName: bi.name,
        reviewText: review.text || review.comment || '',
        rating: review.rating
      });

      const aiReply = await OpenAI.generateReviewReply(
        bi.name,
        review.text || review.comment || '',
        review.rating
      );

      logger.log('📥 [OpenAI] Received reply:', aiReply);
      logger.log('📏 [OpenAI] Reply length:', aiReply?.length || 0, 'characters');

      setReplyText(aiReply);
      logger.log('✅ [OpenAI] Reply set in input field successfully');

    } catch (error) {
      logger.error('❌ Error generating AI reply:', error);
      setReplyText('Thank you for your review! We appreciate your feedback.');
    } finally {
      setGeneratingAI(false);
    }
  };

  // 🤖 AI-powered reply generation using OpenAI (alias for handleAIReply)
  const handleGenerateAIReply = handleAIReply;

  // Handle edit reply
  const handleEditReply = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    logger.log('[ReviewCard] Edit reply clicked');
    setIsEditingReply(true);
    setReplyText(review.reply || '');
    setReplyDialogOpen(true);
  };

  // Handle delete reply
  const handleDeleteReply = async (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!window.confirm('Are you sure you want to delete this reply? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingReply(true);
      logger.log('[ReviewCard] Deleting reply for review:', review.id);

      // Get access token and review name
      const accessToken = await GMBService.getAccessToken();

      // Delete reply from GMB
      await GMBService.deleteReviewReply(accessToken, review.id);

      alert('Reply deleted successfully!');

      // Trigger refresh if callback provided
      if (onReplyUpdate) {
        onReplyUpdate();
      }

    } catch (error) {
      logger.error('Error deleting reply:', error);
      alert('Failed to delete reply. Please try again.');
    } finally {
      setDeletingReply(false);
    }
  };

  return (
    <ReviewContainer>
      <ReviewContent>
        <ReviewHeader>
          <CheckboxContainer>
            <Checkbox
              checked={selected}
              onChange={(e) => onSelect(e.target.checked)}
              sx={{
                border: '1px solid #a0a0aa',
                borderRadius: '2px',
                width: 24,
                height: 24,
                padding: 0
              }}
            />
          </CheckboxContainer>

          <Avatar src={review.avatar} alt={review.author} />

          <Stack sx={{ flex: 1 }}>
            <UserInfo>
              <UserName>{review.author}</UserName>
              <ReviewDate>{review.date}</ReviewDate>
            </UserInfo>

            <RatingRow>
              <MuiRating
                value={review.rating}
                readOnly
                precision={0.1}
                size="small"
                sx={{
                  '& .MuiRating-iconFilled': { color: '#F59E0B' },
                  '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                }}
              />
              <RatingText>{review.rating.toFixed(1)}</RatingText>
              <FromText>from</FromText>
              <GoogleIcon width={69} height={21} />
            </RatingRow>
          </Stack>
        </ReviewHeader>

        <ReviewText>{review.text}</ReviewText>
      </ReviewContent>

      {/* Show existing reply inline if available */}
      {review.reply && (
        <Box sx={{
          ml: { xs: 0, sm: '62px', md: '72px' },
          p: { xs: 1.5, md: 2 },
          backgroundColor: '#EFF6FF',
          borderRadius: '8px',
          borderLeft: '4px solid #0B91D6',
          position: 'relative',
          '&:hover .inline-reply-actions': {
            opacity: 1
          }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ fontSize: { xs: '11px', md: '12px' }, fontWeight: 600, color: '#0B91D6' }}>
              Your Reply:
            </Typography>
            <Box
              className="inline-reply-actions"
              sx={{
                display: 'flex',
                gap: 0.5,
                opacity: { xs: 1, md: 0 },
                transition: 'opacity 0.2s ease'
              }}
            >
              <IconButton
                size="small"
                onClick={handleEditReply}
                sx={{
                  color: '#0B91D6',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: '#DBEAFE',
                    color: '#0980c2'
                  }
                }}
                title="Edit reply"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDeleteReply}
                disabled={deletingReply}
                sx={{
                  color: '#DC2626',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: '#FEE2E2',
                    color: '#B91C1C'
                  },
                  '&:disabled': {
                    color: '#9CA3AF'
                  }
                }}
                title="Delete reply"
              >
                {deletingReply ? (
                  <CircularProgress size={16} sx={{ color: '#DC2626' }} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
          <Typography sx={{ fontSize: { xs: '13px', md: '14px' }, color: '#1F2937', lineHeight: 1.6 }}>
            {review.reply}
          </Typography>
        </Box>
      )}

      <Divider />

      <ActionsRow>
        <ActionButtons>
          <ReplyButton onClick={handleReplyClick}>
            <ReplyIcon width={10} height={12} color="#ffffff" />
            {review.reply ? 'Update Reply' : 'Reply'}
          </ReplyButton>

          <AIReplyButton onClick={handleAIReply}>
            <AIIcon width={13} height={13} color="#fbbc05" />
            Reply with AI
          </AIReplyButton>

          <Button
            onClick={(event) => {
              // Prevent event from bubbling up to parent components
              event.stopPropagation();
              event.preventDefault();
              logger.log('[ReviewCard] Create Post clicked');

              // If onCreatePost callback is provided, use it (for CreatePostPage)
              // Otherwise navigate to /post-generation-results directly (skip font-style page)
              if (onCreatePost) {
                onCreatePost(review);
              } else {
                // Store the selected review in localStorage so PostGenerationResults can use it
                localStorage.setItem('selectedReviewForPost', JSON.stringify(review));
                navigate('/post-generation-results');
              }
            }}
            sx={{
              backgroundColor: '#4caf50',
              borderRadius: '8px',
              color: '#ffffff',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '12px', sm: '13px', md: '14px' },
              fontWeight: 600,
              flex: { xs: 1, sm: 'none' },
              minWidth: { xs: '90px', sm: '120px', md: '140px' },
              minHeight: { xs: '40px', md: '44px' },
              textTransform: 'capitalize',
              padding: { xs: '8px 12px', md: '8px 16px' },
              gap: { xs: '6px', md: '8px' },
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.97)',
                backgroundColor: '#3d9142'
              },
              '&:hover': {
                backgroundColor: '#45a049'
              }
            }}
          >
            Create Post
          </Button>
        </ActionButtons>
      </ActionsRow>

      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleCloseReplyDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1F2937' }}>
            {isEditingReply ? 'Edit Reply' : 'Reply to Review'}
          </Typography>
          <IconButton onClick={handleCloseReplyDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box>
            {/* Reviewer Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <MuiAvatar sx={{
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg, #0B91D6 0%, #0B91D6 100%)',
                fontSize: '20px',
                fontWeight: 700
              }}>
                {review.author?.charAt(0) || 'A'}
              </MuiAvatar>
              <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                  {review.author}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                  {review.date}
                </Typography>
              </Box>
            </Box>

            {/* Rating */}
            <Box sx={{ mb: 3 }}>
              <MuiRating
                value={review.rating}
                readOnly
                precision={0.5}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': { color: '#F59E0B' },
                  '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                }}
              />
            </Box>

            {/* Review Comment */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: '14px', color: '#4B5563', lineHeight: 1.6 }}>
                {review.text || 'No comment provided'}
              </Typography>
            </Box>

            {/* Existing Reply (if any) */}
            {review.reply && !isEditingReply && (
              <Box sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#EFF6FF',
                borderRadius: '8px',
                borderLeft: '4px solid #0B91D6',
                position: 'relative',
                '&:hover .reply-actions': {
                  opacity: 1
                }
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0B91D6' }}>
                    ✅ Your Reply:
                  </Typography>
                  <Box
                    className="reply-actions"
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      opacity: 0,
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={handleEditReply}
                      sx={{
                        color: '#0B91D6',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: '#DBEAFE',
                          color: '#0980c2'
                        }
                      }}
                      title="Edit reply"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleDeleteReply}
                      disabled={deletingReply}
                      sx={{
                        color: '#DC2626',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: '#FEE2E2',
                          color: '#B91C1C'
                        },
                        '&:disabled': {
                          color: '#9CA3AF'
                        }
                      }}
                      title="Delete reply"
                    >
                      {deletingReply ? (
                        <CircularProgress size={16} sx={{ color: '#DC2626' }} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#1F2937', lineHeight: 1.6 }}>
                  {review.reply}
                </Typography>
              </Box>
            )}

            {/* Reply Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                  {isEditingReply ? 'Edit your reply:' : 'Write your reply:'}
                </Typography>
                {generatingAI && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#9c27b0' }} />
                    <Typography sx={{ fontSize: '13px', color: '#9c27b0', fontWeight: 500 }}>
                      Generating...
                    </Typography>
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                multiline
                rows={2}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={generatingAI ? "AI is generating your reply..." : "Write your reply here..."}
                disabled={generatingAI}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '14px',
                    '&:hover fieldset': { borderColor: '#0B91D6' },
                    '&.Mui-focused fieldset': { borderColor: '#0B91D6' }
                  }
                }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={generatingAI ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateAIReply}
                  disabled={generatingAI}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#9c27b0',
                    color: '#9c27b0',
                    '&:hover': { borderColor: '#7b1fa2', backgroundColor: 'rgba(156, 39, 176, 0.04)' }
                  }}
                >
                  {generatingAI ? 'Generating...' : '✨ Generate AI Reply'}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={handleCloseReplyDialog}
            sx={{ textTransform: 'none', color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendReply}
            disabled={!replyText.trim() || submittingReply}
            startIcon={submittingReply ? <CircularProgress size={16} /> : null}
            sx={{
              textTransform: 'none',
              backgroundColor: isEditingReply ? '#F59E0B' : '#0B91D6',
              '&:hover': { backgroundColor: isEditingReply ? '#D97706' : '#0980c2' },
              '&:disabled': { backgroundColor: '#E5E7EB' }
            }}
          >
            {submittingReply ? (isEditingReply ? 'Updating...' : 'Submitting...') : (isEditingReply ? 'Update Reply' : 'Submit Reply')}
          </Button>
        </DialogActions>
      </Dialog>
    </ReviewContainer>
  );
};

export default ReviewCard;